import type { ReactNode } from "react";
import { Settings2 } from "lucide-react";

import { useTheme } from "@/components/providers/theme-provider";
import { usePreferences } from "@/components/providers/preferences-provider";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useSidebar } from "@/components/ui/sidebar";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import type { AdminPreferences } from "@/lib/preferences";

function Row<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: ReactNode }[];
  onChange: (value: T) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium">{label}</Label>
      <ToggleGroup
        size="sm"
        spacing={0}
        variant="outline"
        className="w-full"
        value={[value]}
        onValueChange={([next]) => {
          if (next) onChange(next as T);
        }}
      >
        {options.map((option) => (
          <ToggleGroupItem
            key={option.value}
            value={option.value}
            className="flex-1 text-xs"
            aria-label={`${label}: ${String(option.label)}`}
          >
            {option.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );
}

/** Appearance & layout preferences (theme, sidebar style/behaviour, content width, header). */
export function LayoutControls() {
  const { preferences, setPreference, reset } = usePreferences();
  const { theme = "light", setTheme } = useTheme();
  const { open, setOpen } = useSidebar();

  const set =
    <K extends keyof AdminPreferences>(key: K) =>
    (value: AdminPreferences[K]) =>
      setPreference(key, value);

  return (
    <Popover>
      <PopoverTrigger
        render={<Button variant="ghost" size="icon" aria-label="Layout and appearance" />}
      >
        <Settings2 />
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        <PopoverHeader>
          <PopoverTitle>Preferences</PopoverTitle>
          <PopoverDescription>
            Personalise how the dashboard looks on this device.
          </PopoverDescription>
        </PopoverHeader>
        <div className="mt-4 space-y-3.5">
          <Row
            label="Theme"
            value={theme as "light" | "dark" | "system"}
            onChange={setTheme}
            options={[
              { value: "light", label: "Light" },
              { value: "dark", label: "Dark" },
              { value: "system", label: "System" },
            ]}
          />
          <Row
            label="Sidebar"
            value={open ? "expanded" : "collapsed"}
            onChange={(value) => setOpen(value === "expanded")}
            options={[
              { value: "expanded", label: "Expanded" },
              { value: "collapsed", label: "Hidden" },
            ]}
          />
          <Row
            label="Sidebar style"
            value={preferences.sidebarVariant}
            onChange={set("sidebarVariant")}
            options={[
              { value: "inset", label: "Inset" },
              { value: "sidebar", label: "Sidebar" },
              { value: "floating", label: "Floating" },
            ]}
          />
          <Row
            label="When collapsed"
            value={preferences.sidebarCollapsible}
            onChange={set("sidebarCollapsible")}
            options={[
              { value: "icon", label: "Show icons" },
              { value: "offcanvas", label: "Hide fully" },
            ]}
          />
          <Row
            label="Content width"
            value={preferences.contentLayout}
            onChange={set("contentLayout")}
            options={[
              { value: "centered", label: "Centered" },
              { value: "full-width", label: "Full width" },
            ]}
          />
          <Row
            label="Header"
            value={preferences.navbarStyle}
            onChange={set("navbarStyle")}
            options={[
              { value: "sticky", label: "Sticky" },
              { value: "scroll", label: "Scrolls away" },
            ]}
          />
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => {
              reset();
              setTheme("light");
              setOpen(true);
            }}
          >
            Restore defaults
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
